import React, { useEffect, useState } from 'react'
import { Box, Button, Fab, Typography, CircularProgress } from '@mui/material'
import {Visibility, ArrowBack} from '@mui/icons-material'
import { StepHeader, StepLoader } from '../../components'
import { stepperStyles } from '../styles'
import {
  getAvailablePositions,
  getTakenPositions,
  getInventoryCheckUserDetail,
  patchInventoryCheckCycle as patchCycleApi
} from '../../api'
import { useApi } from '../../../../hooks/useApi'
import { Status } from '../../../../hooks/useFetchState'
import { ApiResponse } from '../../../../typings/ApiResponse'
import { StepProps } from '../../../../components/_WMS/Stepper'
import {
  eInventoryCheckSteps,
  InventoryCheckDetail,
  InventoryCheckPosition,
  SharedStateType
} from '../../types'

interface InventoryCheckUserSummary {
  inventoryCheckId?: number
  warehouseName?: string
  totalPosition?: number
  pendingPositions?: number
  auditedPositions?: number
  progress?: number
  tag?: string
  details?: InventoryCheckDetail[]
}

interface ShowInventoryCheckPositionsProps extends StepProps<SharedStateType> {}


export const ShowInventoryCheckPositions: React.FC<
  ShowInventoryCheckPositionsProps
> = ({
  goToStep,
  next,
  setSharedState,
  sharedState: { workOrderId, user }
}) => {
  const { state, callApi } = useApi<ApiResponse<InventoryCheckPosition>>()
  const {
    state: userAssignedPositionsState,
    callApi: userAssignedPositionsCallApi
  } = useApi<ApiResponse<InventoryCheckPosition>>()
  const {
    state: patchInventoryCheckState,
    callApi: patchInventoryCheckCallApi
  } = useApi()
  const { state: summaryState, callApi: summaryCallApi } =
    useApi<InventoryCheckUserSummary>()

  const [countdown, setCountdown] = useState(3)

  const requestSummary = () => {
    if (workOrderId) {
      summaryCallApi(
        getInventoryCheckUserDetail(workOrderId, user?.userId)
      ).catch(() => undefined)
    }
  }

  const requestGetInventoryCheckPositions = () => {
    if (workOrderId) {
      callApi(getAvailablePositions(workOrderId, user?.userId)).catch(
        (err) => {
          setSharedState((prevState) => ({ ...prevState, error: err }))
          goToStep(eInventoryCheckSteps.Error)
        }
      )
    }
  }

  const requestGetInventoryCheckUserAssignedPositions = () => {
    if (workOrderId) {
      userAssignedPositionsCallApi(
        getTakenPositions(workOrderId, user?.userId)
      ).catch((err) => {
        setSharedState((prevState) => ({ ...prevState, error: err }))
        goToStep(eInventoryCheckSteps.Error)
      })
    }
  }

  const requestPatchInventoryCheckCycle = (
    inventoryCheckId: number,
    positionId: number
  ) => {
    patchInventoryCheckCallApi(
      patchCycleApi(inventoryCheckId, positionId)
    )
      .then((res: InventoryCheckDetail) =>
        setSharedState((prevState) => ({
          ...prevState,
          inventoryCheckDetails: res,
          inventoryCheckDetailId: res.inventoryCheckDetailId
        }))
      )
      .then(next)
      .catch((err) => {
        setSharedState((prevState) => ({ ...prevState, error: err }))
        goToStep(eInventoryCheckSteps.Error)
      })
  }

  useEffect(() => {
    setSharedState((p) => ({ ...p, stepTitle: 'Listado de ubicaciones', hideBackButton: true }))
    return () => {
      setSharedState((p) => ({ ...p, stepTitle: undefined, hideBackButton: false }))
    }
  }, [])


  useEffect(() => {
    requestSummary()
    requestGetInventoryCheckUserAssignedPositions()
  }, [])

  useEffect(() => {
    if (
      userAssignedPositionsState.data !== undefined &&
      userAssignedPositionsState.data?.items.length === 0
    ) {
      requestGetInventoryCheckPositions()
    }
  }, [userAssignedPositionsState.data])

  useEffect(() => {
    if (state.data?.items.length === 0 && state.status === Status.Success) {
      setCountdown(3)
      const countdownInterval = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(countdownInterval)
            return 0
          }
          return prev - 1
        })
      }, 1000)
      const timer = setTimeout(() => {
        setSharedState({
          workOrderId: undefined,
          positionCode: undefined,
          error: '',
          user
        })
        goToStep(eInventoryCheckSteps.SearchInventoryCheck)
      }, 3000)
      return () => {
        clearTimeout(timer)
        clearInterval(countdownInterval)
      }
    }
    return undefined
  }, [state.data, state.status])

  const handleSelectPosition = async (position: InventoryCheckPosition) => {
    setSharedState((prevState) => ({
      ...prevState,
      positionCode: position.positionName,
      positionId: position.positionId,
      zoneName: position.zoneName,
      warehouseName: position.warehouseName
    }))
    await requestPatchInventoryCheckCycle(workOrderId!, position.positionId)
  }

  const summary = summaryState.data
  const totalPosition = summary?.totalPosition ?? 0
  const auditadas = summary?.details
    ? (() => {
        const byPosition = new Map<string, typeof summary.details>()
        summary.details!.forEach((d) => {
          byPosition.set(d.positionName, [
            ...(byPosition.get(d.positionName) ?? []),
            d
          ])
        })
        let count = 0
        byPosition.forEach((cycles) => {
          if (cycles.every((c) => c.endDate !== null)) count++
        })
        return count
      })()
    : 0
  const pendientes = totalPosition - auditadas

  const allPositions = [
    ...(userAssignedPositionsState.data?.items ?? []),
    ...(state.data?.items ?? [])
  ]

  const isPatching = patchInventoryCheckState.status === Status.Loading
  const isLoadingPositions =
    state.status === Status.Loading ||
    userAssignedPositionsState.status === Status.Loading
  const noPositions =
    state.data?.items.length === 0 && state.status === Status.Success

  return (
    <Box sx={stepperStyles.root}>
      {isPatching && <StepLoader message='Asignando posición...' />}
      <Box
        sx={stepperStyles.content}
        style={{
          justifyContent: 'flex-start',
          paddingTop: 24,
          overflowY: 'auto'
        }}
      >
        <Box style={{ width: '100%', padding: '0 16px' }}>

          {/* Fila de botones: Volver | Ver detalle */}
          <Box style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <Fab
              variant='extended'
              size='medium'
              onClick={() => goToStep(eInventoryCheckSteps.SearchInventoryCheck)}
              style={{
                borderRadius: 12,
                height: 50,
                minWidth: 110,
                paddingLeft: 10,
                paddingRight: 14,
                backgroundColor: 'transparent',
                border: '1px solid #E0E0E0',
                boxShadow: 'none',
                fontSize: 14,
                fontWeight: 600,
                color: '#666',
                gap: 2,
                textTransform: 'none'
              }}
            >
              <ArrowBack style={{ fontSize: 18 }} />
              Volver
            </Fab>

            <Fab
              variant='extended'
              size='medium'
              onClick={() => goToStep(eInventoryCheckSteps.AuditDetail)}
              style={{
                borderRadius: 12,
                height: 50,
                minWidth: 110,
                paddingLeft: 10,
                paddingRight: 14,
                backgroundColor: 'transparent',
                border: '1px solid rgb(224, 224, 224)',
                boxShadow: 'none',
                fontSize: 14,
                fontWeight: 600,
                color: '#666',
                gap: 2,
                textTransform: 'none'
              }}
            >
              <Visibility  sx={{fontSize: 18, color:'#1976d2'}} />
              Ver detalle
            </Fab>
          </Box>

          <StepHeader
            loading={summaryState.status === Status.Loading}
            fields={[
              { label: 'Conteo', value: workOrderId ?? '—' },
              { label: 'Almacén', value: summary?.warehouseName }
            ]}
          />

          {/* Stats de ubicaciones */}
          {totalPosition > 0 && (
            <Box style={{ marginBottom: 16 }}>
              <Typography
                variant='caption'
                style={{
                  color: '#999',
                  fontWeight: 700,
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                  display: 'block',
                  marginBottom: 8
                }}
              >
                Ubicaciones
              </Typography>
              <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
                {[
                  { label: 'Pendientes', value: pendientes ?? '—', color: '#3B6D11' },
                  { label: 'Auditadas', value: auditadas ?? '—', color: '#185FA5' },
                  { label: 'Totales', value: totalPosition, color: '#333' }
                ].map(({ label, value, color }) => (
                  <Box
                    key={label}
                    style={{
                      backgroundColor: '#f5f5f5',
                      borderRadius: 8,
                      padding: '6px 8px',
                      textAlign: 'center'
                    }}
                  >
                    <Typography style={{ fontSize: 10, color: '#999', lineHeight: 1.3 }}>
                      {label}
                    </Typography>
                    <Typography style={{ fontSize: 20, fontWeight: 500, color, lineHeight: 1.2 }}>
                      {value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Lista de posiciones */}
          <Typography
            variant='caption'
            style={{
              color: '#999',
              fontWeight: 700,
              letterSpacing: 1,
              textTransform: 'uppercase',
              display: 'block',
              marginBottom: 8
            }}
          >
            Selecciona ubicación a auditar
          </Typography>

          {isLoadingPositions && (
            <Box style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {noPositions && (
            <Box style={{ textAlign: 'center', marginTop: 16 }}>
              <Typography variant='body2' style={{ color: '#888' }}>
                No existen posiciones disponibles para procesar
              </Typography>
              <Typography variant='body2' style={{ color: '#888', marginTop: 8 }}>
                Volviendo en {countdown}...
              </Typography>
            </Box>
          )}

          {!isLoadingPositions && (
            <Box style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {allPositions.map((position) => (
                <Button
                  key={position.positionId}
                  fullWidth
                  variant='outlined'
                  color='inherit'
                  onClick={() => handleSelectPosition(position)}
                  disabled={isPatching}
                  style={{
                    borderRadius: 12,
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: 16,
                    padding: '14px 0',
                    background: '#f5f5f5',
                    borderColor: '#e0e0e0',
                    color: '#666'
                  }}
                >
                  {position.positionName}
                </Button>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}
